import React, { useEffect, useMemo, useState } from "react";
import { Button, Image, Input, Modal, Space, Table } from "antd";
import type { TableColumnsType } from "antd";
import { FileImageOutlined, SearchOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { ReadonlyRangePicker } from "../../../components/common/DatePickerControls";
import {
    FilterActions,
    FilterBar,
    FilterField,
} from "../../../components/common/FilterBar";
import {
    buildPaginatedListPagination,
    PAGINATED_LIST_TABLE_SIZE,
} from "../../../components/common/PaginatedListTable";
import { ReadonlyDetectionOverlayImage } from "../../../components/common/ReadonlyDetectionOverlayImage";
import { localeService } from "../../../services/localeService";
import type { HistoryListItem } from "../../../types/history";

export interface ValidationDatasetHistoryFilters {
    deviceName?: string;
    endTimestamp?: number;
    filename?: string;
    startTimestamp?: number;
}

interface ValidationDatasetHistoryPickerProps {
    excludedIds: string[];
    items: HistoryListItem[];
    loading: boolean;
    maxSelectable: number;
    onClose: () => void;
    onConfirm: () => void;
    onPageChange: (page: number, pageSize: number) => void;
    onSearch: (filters: ValidationDatasetHistoryFilters) => void;
    onSelectionChange: (keys: React.Key[]) => void;
    open: boolean;
    page: number;
    pageSize: number;
    saving: boolean;
    selectedIds: React.Key[];
    total: number;
}

type TimeRange = [Dayjs | null, Dayjs | null] | null;

const ValidationDatasetHistoryPicker: React.FC<
    ValidationDatasetHistoryPickerProps
> = ({
    excludedIds,
    items,
    loading,
    maxSelectable,
    onClose,
    onConfirm,
    onPageChange,
    onSearch,
    onSelectionChange,
    open,
    page,
    pageSize,
    saving,
    selectedIds,
    total,
}) => {
    const { t } = useTranslation(["admin", "common"]);
    const [deviceName, setDeviceName] = useState("");
    const [filename, setFilename] = useState("");
    const [timeRange, setTimeRange] = useState<TimeRange>(null);
    const excludedIdSet = useMemo(() => new Set(excludedIds), [excludedIds]);

    useEffect(() => {
        if (!open) {
            setDeviceName("");
            setFilename("");
            setTimeRange(null);
        }
    }, [open]);

    const submitSearch = () => {
        onSearch({
            filename: filename.trim() || undefined,
            deviceName: deviceName.trim() || undefined,
            startTimestamp: timeRange?.[0]?.valueOf(),
            endTimestamp: timeRange?.[1]?.valueOf(),
        });
    };

    const resetSearch = () => {
        setFilename("");
        setDeviceName("");
        setTimeRange(null);
        onSearch({});
    };

    const columns: TableColumnsType<HistoryListItem> = [
        {
            title: t("admin:modelValidation.columns.preview"),
            dataIndex: "thumbnail",
            width: 116,
            render: (value: string | null | undefined, record) => {
                if (
                    record.source_image &&
                    Array.isArray(record.review_predictions)
                ) {
                    return (
                        <ReadonlyDetectionOverlayImage
                            alt={record.filename}
                            colorizePredictions
                            height={56}
                            imageSrc={record.source_image}
                            predictions={record.review_predictions}
                            preview
                            previewMaskClosable={false}
                            previewTitle={record.filename}
                            width={84}
                        />
                    );
                }
                return value ? (
                    <Image
                        alt={record.filename}
                        height={56}
                        preview={{ maskClosable: false }}
                        src={value}
                        style={{ objectFit: "cover" }}
                        width={84}
                    />
                ) : (
                    <div
                        aria-label={t(
                            "admin:modelValidation.datasets.previewUnavailable",
                        )}
                        style={{
                            alignItems: "center",
                            background: "#f5f5f5",
                            color: "#8c8c8c",
                            display: "flex",
                            height: 56,
                            justifyContent: "center",
                            width: 84,
                        }}
                    >
                        <FileImageOutlined />
                    </div>
                );
            },
        },
        {
            title: t("admin:modelValidation.columns.imageName"),
            dataIndex: "filename",
            ellipsis: true,
        },
        {
            title: t("admin:modelValidation.columns.device"),
            dataIndex: "device_name",
            width: 180,
            render: (value, record) => value || record.device_id || "-",
        },
        {
            title: t("admin:modelValidation.columns.time"),
            dataIndex: "timestamp",
            width: 190,
            render: (value: number) => localeService.formatDateTime(value),
        },
    ];

    return (
        <Modal
            confirmLoading={saving}
            destroyOnClose
            maskClosable={false}
            okButtonProps={{ disabled: selectedIds.length === 0 }}
            onCancel={onClose}
            onOk={onConfirm}
            open={open}
            title={t("admin:modelValidation.datasets.historyPickerTitle")}
            width="min(1120px, 96vw)"
        >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <FilterBar>
                    <FilterField
                        label={t("admin:modelValidation.filters.imageName")}
                    >
                        <Input
                            allowClear
                            onChange={(event) =>
                                setFilename(event.target.value)
                            }
                            onPressEnter={submitSearch}
                            placeholder={t(
                                "admin:modelValidation.datasets.imageNamePlaceholder",
                            )}
                            value={filename}
                        />
                    </FilterField>
                    <FilterField
                        label={t("admin:modelValidation.filters.device")}
                    >
                        <Input
                            allowClear
                            onChange={(event) =>
                                setDeviceName(event.target.value)
                            }
                            onPressEnter={submitSearch}
                            placeholder={t(
                                "admin:modelValidation.datasets.devicePlaceholder",
                            )}
                            value={deviceName}
                        />
                    </FilterField>
                    <FilterField
                        label={t("admin:modelValidation.filters.time")}
                    >
                        <ReadonlyRangePicker
                            onChange={(value) => setTimeRange(value)}
                            showTime
                            value={timeRange}
                        />
                    </FilterField>
                    <FilterActions>
                        <Button onClick={resetSearch}>
                            {t("common:buttons.reset")}
                        </Button>
                        <Button
                            icon={<SearchOutlined />}
                            loading={loading}
                            onClick={submitSearch}
                            type="primary"
                        >
                            {t("common:buttons.search")}
                        </Button>
                    </FilterActions>
                </FilterBar>
                <Table
                    columns={columns}
                    dataSource={items}
                    loading={loading}
                    pagination={buildPaginatedListPagination({
                        current: page,
                        pageSize,
                        total,
                        showTotal: (count) =>
                            t("admin:modelValidation.pagination.totalImages", {
                                total: count,
                            }),
                        onChange: onPageChange,
                    })}
                    rowKey="id"
                    rowSelection={{
                        getCheckboxProps: (record) => ({
                            disabled:
                                excludedIdSet.has(record.id) ||
                                (!selectedIds.includes(record.id) &&
                                    selectedIds.length >= maxSelectable),
                        }),
                        preserveSelectedRowKeys: true,
                        selectedRowKeys: selectedIds,
                        onChange: (keys) =>
                            onSelectionChange(
                                keys
                                    .filter(
                                        (key) =>
                                            !excludedIdSet.has(String(key)),
                                    )
                                    .slice(0, maxSelectable),
                            ),
                    }}
                    scroll={{ x: 860 }}
                    size={PAGINATED_LIST_TABLE_SIZE}
                />
            </Space>
        </Modal>
    );
};

export default ValidationDatasetHistoryPicker;
